<script setup>
import { ref, computed, toRef } from 'vue';

const props = defineProps({
  modelValue: { type: [Array, Object, String, Number, null], default: null },
  options: { type: Array, required: true },

  title: { type: String, default: 'Sélectionnez une option' },
  description: { type: String, default: '' },

  multiple: { type: Boolean, default: false },
  searchable: { type: Boolean, default: false },

  mode: { type: String, default: 'dialog' },
  // "dialog" | "dropdown" | "inline"

  selectOnClick: { type: Boolean, default: true }
});

const emit = defineEmits([
  'update:modelValue',
  'select',
  'activate',
  'deactivate'
]);

// -----------------------------------------
// STATE
// -----------------------------------------
const search = ref('');
const dropdownOpen = ref(false);
const modal = ref(null);

const activeIndex = ref(null);

// expose open/close for parent
function open() {
  if (props.mode === 'dialog') modal.value?.show();
  else dropdownOpen.value = true;
}
function close() {
  if (props.mode === 'dialog') modal.value?.hide();
  else dropdownOpen.value = false;
}

defineExpose({ open, close });

// -----------------------------------------
// FILTERED OPTIONS
// -----------------------------------------
const filteredOptions = computed(() => {
  if (!props.searchable || !search.value.trim()) return props.options;

  return props.options.filter((o) =>
    o.name.toLowerCase().includes(search.value.toLowerCase())
  );
});

// -----------------------------------------
// SELECT LOGIC
// -----------------------------------------
function isSelected(option) {
  if (props.multiple) {
    return Array.isArray(props.modelValue)
      ? props.modelValue.some((v) => v.value === option.value)
      : false;
  }
  return props.modelValue?.value === option.value;
}

function toggleOption(option, index) {
  activeIndex.value = index;

  emit('activate', option);

  if (props.multiple) {
    const current = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];

    const exists = current.find((o) => o.value === option.value);

    if (exists) {
      const newList = current.filter((o) => o.value !== option.value);
      emit('update:modelValue', newList);
      emit('deactivate', option);
    } else {
      const newList = [...current, option];
      emit('update:modelValue', newList);
      emit('select', option);
    }

    return;
  }

  // single
  if (props.selectOnClick) {
    emit('update:modelValue', option);
    emit('select', option);
    if (props.mode !== 'inline') close();
  }
}

// -----------------------------------------
// RENDER WRAPPER (dialog or dropdown)
// -----------------------------------------

const wrapperTag = computed(() => {
  if (props.mode === 'inline') return 'div';
  if (props.mode === 'dropdown') return 'div';
  return 'DialogComponent';
});
</script>

<template>
  <!-- WRAPPER -->
  <component
    :is="wrapperTag"
    ref="modal"
    v-bind="
      props.mode === 'dialog'
        ? { type: 'fullscreen', noInnerPadding: true, hideCloseButton: true }
        : {}
    "
    class="relative w-full"
  >
    <!-- TRIGGER (dropdown or dialog) -->
    <template v-if="mode !== 'inline'">
      <slot name="trigger" :open="open">
        <button
          class="w-full p-4 rounded-xl bg-white border text-left"
          @click="open"
        >
          {{
            multiple
              ? (modelValue?.length || 0) + ' sélection(s)'
              : modelValue?.name || title
          }}
        </button>
      </slot>
    </template>

    <!-- CONTENT -->
    <div
      v-if="mode === 'inline' || dropdownOpen || mode === 'dialog'"
      class="w-full flex flex-col overflow-auto p-4 gap-2 bg-slate-50 max-h-[75vh]"
      :class="{
        'border rounded-xl absolute z-50 bg-white shadow-xl p-4':
          mode === 'dropdown'
      }"
    >
      <!-- Close button (dialog only) -->
      <button
        v-if="mode === 'dialog'"
        class="w-12 h-12 mb-2 p-3 rounded-full bg-slate-200"
        @click="close"
      >
        <ColorImg
          class="w-full h-full bg-primary -rotate-90"
          src="/images/all/arrow-up.svg"
        />
      </button>

      <!-- TITLE -->
      <div class="text-primary text-3xl pr-8">
        {{ title }}
      </div>

      <!-- DESCRIPTION -->
      <div v-if="description" class="text-primary-200">
        {{ description }}
      </div>

      <!-- SEARCH -->
      <input
        v-if="searchable"
        v-model="search"
        placeholder="Rechercher..."
        class="w-full px-4 py-3 border rounded-xl bg-white"
      />

      <!-- OPTIONS -->
      <div class="flex flex-col gap-4 overflow-auto">
        <div
          v-for="(option, i) in filteredOptions"
          :key="i"
          @click="toggleOption(option, i)"
          class="flex items-center gap-4 p-4 rounded-2xl border bg-white cursor-pointer transition"
          :class="{ 'border-primary': isSelected(option) }"
        >
          <div></div>
          <div
            v-if="option.icon || option.image"
            class="w-14 h-14 overflow-hidden rounded-full bg-slate-100"
          >
            <component
              :is="option.icon ? 'ColorImg' : 'ImgComponent'"
              :src="option.image"
              class="w-full h-full object-cover"
            />
          </div>

          <!-- DEFAULT TEXT or CUSTOM SLOT -->
          <div class="flex flex-col grow">
            <slot name="option" :option="option" :selected="isSelected(option)">
              <div class="text-2xl text-primary">{{ option.name }}</div>
              <div class="text-lg text-neutral-500">
                {{ option.description }}
              </div>
            </slot>
          </div>

          <!-- Badge -->
          <div v-if="selectOnClick">
            <ColorImg
              src="/images/all/right-arrow.svg"
              class="bg-primary-200"
            />
          </div>
          <div
            v-else-if="isSelected(option)"
            class="text-primary-600 font-bold text-2xl"
          >
            ✓
          </div>
        </div>
      </div>
    </div>
  </component>
</template>
